import { useMemo, useState } from "react";
import { useReadyAuth } from "@/auth/use-auth";
import { useContacts, useContactMutations } from "@/features/contacts/hooks";
import { ContactsBand } from "@/features/contacts/components/ContactsBand";
import { ContactsFilters } from "@/features/contacts/components/ContactsFilters";
import { ContactCard } from "@/features/contacts/components/ContactCard";
import { ContactDetailModal } from "@/features/contacts/components/ContactDetailModal";
import { ContactFormModal } from "@/features/contacts/components/ContactFormModal";
import { UploadCsvModal } from "@/features/contacts/components/UploadCsvModal";
import { downloadContactsCsv } from "@/features/contacts/export-csv";
import { useAssignableUsers } from "@/features/signals/hooks";
import {
  applyFilters,
  followUpDueCount,
  activeEngagementCount,
  scopeStats,
  sortContacts,
  sourceGroupCounts,
  temperatureCounts,
  uniqueCompanies,
  uniqueOwners,
  uniqueSourceGroups,
  type ContactFilters,
} from "@/features/contacts/derive";
import type {
  Contact,
  ContactInput,
  ContactLogMethod,
  ContactPatch,
  ContactScope,
  ContactTemperature,
} from "@/features/contacts/types";

/**
 * Contacts surface (live) - the sealed sx- Session D design, wired to the live
 * adapter (Codex v08 contact Edge Functions). Deep-teal band, real admin scope,
 * five-field search, temperature + follow-up-due + source-group pills, attribute
 * filters, sorted card grid, and a detail/form/upload modal set. Reads +
 * add/edit/delete/export/import are wired; temperature-set, reminder-set, and
 * log-contact write paths are not live and ship disabled-honest.
 */

const PAGE_SIZE = 48;
const SCOPE_CAP = 200;

const DEFAULT_FILTERS: ContactFilters = {
  search: "",
  temperature: "all",
  sourceGroup: "all",
  followUpDue: false,
  activeEngagement: false,
  seniority: [],
  functions: [],
  company: "",
  titleContains: "",
  ownerId: "all",
};

export function Contacts(): JSX.Element {
  const { profile } = useReadyAuth();
  const viewerId = profile.id;
  const isAdmin = profile.role === "admin" || profile.role === "super_admin";

  const [scope, setScope] = useState<ContactScope>("mine");
  const [filters, setFilters] = useState<ContactFilters>(DEFAULT_FILTERS);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [form, setForm] = useState<{ contact: Contact | null } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const effectiveScope: ContactScope = isAdmin ? scope : "mine";

  const { rows, total, allLoaded, initialLoading, initialError, partialFailure, retry } =
    useContacts(effectiveScope);
  const { add, update, log, remove, previewImport, confirmImport } = useContactMutations();
  const assignableUsers = useAssignableUsers();

  const stats = useMemo(() => scopeStats(rows), [rows]);
  const filtered = useMemo(() => sortContacts(applyFilters(rows, filters)), [rows, filters]);
  const tempCounts = useMemo(() => temperatureCounts(rows, filters), [rows, filters]);
  const followUpCount = useMemo(() => followUpDueCount(rows, filters), [rows, filters]);
  const activeEngCount = useMemo(() => activeEngagementCount(rows, filters), [rows, filters]);
  const sourceCounts = useMemo(() => sourceGroupCounts(rows, filters), [rows, filters]);
  const sourceGroups = useMemo(() => uniqueSourceGroups(rows), [rows]);
  const companies = useMemo(() => uniqueCompanies(rows), [rows]);
  const owners = useMemo(() => uniqueOwners(rows, viewerId), [rows, viewerId]);

  const capped = filtered.slice(0, SCOPE_CAP);
  const shownList = capped.slice(0, visible);
  const showOwner = isAdmin && effectiveScope === "all";
  const openContact = detailId ? (rows.find((c) => c.id === detailId) ?? null) : null;

  const patchFilters = (patch: Partial<ContactFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setVisible(PAGE_SIZE);
  };
  const changeScope = (next: ContactScope) => {
    setScope(next);
    setFilters((f) => ({ ...f, ownerId: "all" }));
    setVisible(PAGE_SIZE);
  };
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setVisible(PAGE_SIZE);
  };

  const submitForm = (input: ContactInput) => {
    const editing = form?.contact ?? null;
    const done = () => setForm(null);
    if (editing) {
      // The form owns identity + provenance fields only; temperature and the
      // reminder are set from the detail modal, so they are left out of the
      // patch (omitted = unchanged) rather than re-asserted.
      const patch: ContactPatch = {
        name: input.name,
        title: input.title,
        company: input.company,
        email: input.email,
        phone: input.phone,
        notes: input.notes,
        linkedin: input.linkedin,
        eventSource: input.eventSource,
        dateMet: input.dateMet,
      };
      update.mutate({ id: editing.id, patch }, { onSuccess: done });
    } else {
      add.mutate(input, { onSuccess: done });
    }
  };

  const removeContact = (contact: Contact) => {
    remove.mutate(contact.id);
    if (detailId === contact.id) setDetailId(null);
  };

  const saveNotes = (notes: string) => {
    if (!openContact) return;
    update.mutate({ id: openContact.id, patch: { notes } });
  };

  const setTemperature = (temperature: ContactTemperature) => {
    if (!openContact) return;
    update.mutate({ id: openContact.id, patch: { temperature } });
  };

  const logContact = (method: ContactLogMethod) => {
    if (!openContact) return;
    log.mutate({ id: openContact.id, method });
  };

  const saveReminder = (reminder: { date: string; time: string | null; reason: string | null }) => {
    if (!openContact) return;
    update.mutate({
      id: openContact.id,
      patch: {
        reminderDate: reminder.date,
        reminderTime: reminder.time,
        reminderReason: reminder.reason,
      },
    });
  };

  const clearReminder = () => {
    if (!openContact) return;
    update.mutate({
      id: openContact.id,
      patch: { reminderDate: null, reminderTime: null, reminderReason: null },
    });
  };

  const onExport = () => {
    downloadContactsCsv(filtered, effectiveScope);
    setNote(
      `Exported ${filtered.length.toLocaleString()} contact${filtered.length === 1 ? "" : "s"} (current scope and filters).`
    );
  };

  return (
    <div className="sxc">
      <ContactsBand
        total={total}
        stats={stats}
        allLoaded={allLoaded}
        isAdmin={isAdmin}
        scope={effectiveScope}
        onScopeChange={changeScope}
        onUpload={() => setUploadOpen(true)}
        onExport={onExport}
        onAdd={() => setForm({ contact: null })}
        uploadBusy={previewImport.isPending}
        exportDisabled={rows.length === 0}
      />

      {note ? (
        <div className="sxc-note" role="status">
          <span>{note}</span>
          <button
            type="button"
            className="sxc-note-close"
            aria-label="Dismiss"
            onClick={() => setNote(null)}
          >
            ✕
          </button>
        </div>
      ) : null}

      <ContactsFilters
        filters={filters}
        onChange={patchFilters}
        tempCounts={tempCounts}
        followUpCount={followUpCount}
        activeEngagementCount={activeEngCount}
        sourceCounts={sourceCounts}
        sourceGroups={sourceGroups}
        companies={companies}
        owners={owners}
        showOwnerFilter={showOwner}
        total={filtered.length}
        shown={shownList.length}
        cap={SCOPE_CAP}
      />

      {allLoaded &&
      total != null &&
      total > rows.length &&
      filters.search.trim() === "" &&
      filters.temperature === "all" &&
      filters.sourceGroup === "all" &&
      !filters.followUpDue &&
      !filters.activeEngagement ? (
        <p className="sxc-count sxc-pagination-note" role="status">
          {total.toLocaleString()} contacts total; {rows.length.toLocaleString()} loaded.{" "}
          {(total - rows.length).toLocaleString()} could not be retrieved (a known pagination issue
          is being fixed). Counts and tiles reflect the loaded set.
        </p>
      ) : null}

      {initialLoading ? (
        <div className="sxc-empty">
          <p>Loading contacts...</p>
        </div>
      ) : initialError ? (
        <div className="sxc-empty">
          <h2>Could not load contacts</h2>
          <button type="button" className="sx-btn sx-btn-p2" onClick={retry}>
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="sxc-empty">
          <h2>{rows.length === 0 ? "No contacts yet" : "No contacts match these filters"}</h2>
          <p>
            {rows.length === 0
              ? "Add a contact or upload a CSV to start your watchlist. Contacts matched to live signals surface here first."
              : "Try widening the search, temperature, or attribute filters."}
          </p>
          {rows.length === 0 ? (
            <button
              type="button"
              className="sx-btn sx-btn-p2"
              onClick={() => setForm({ contact: null })}
            >
              Add a contact
            </button>
          ) : (
            <button type="button" className="sx-btn sx-btn-p2" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="sxc-grid">
            {shownList.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                showOwner={showOwner}
                viewerId={viewerId}
                onOpen={(contact) => setDetailId(contact.id)}
              />
            ))}
          </div>

          {shownList.length < capped.length ? (
            <div className="sxc-more-row">
              <button
                type="button"
                className="sx-btn-more"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
              >
                Show {Math.min(PAGE_SIZE, capped.length - shownList.length)} more
              </button>
            </div>
          ) : !allLoaded && !partialFailure ? (
            <div className="sxc-more-row">
              <span className="sxc-count">Loading more contacts...</span>
            </div>
          ) : null}

          {partialFailure ? (
            <div className="sxc-more-row">
              <button type="button" className="sx-btn sx-btn-p2" onClick={retry}>
                Retry loading the rest
              </button>
            </div>
          ) : null}
        </>
      )}

      {openContact ? (
        <ContactDetailModal
          contact={openContact}
          viewerId={viewerId}
          isAdmin={isAdmin}
          onClose={() => setDetailId(null)}
          onEdit={(contact) => {
            setDetailId(null);
            setForm({ contact });
          }}
          onRemove={removeContact}
          onSaveNotes={saveNotes}
          onSetTemperature={setTemperature}
          onLogContact={logContact}
          onSaveReminder={saveReminder}
          onClearReminder={clearReminder}
          mutating={update.isPending}
          logging={log.isPending}
        />
      ) : null}

      {form ? (
        <ContactFormModal
          contact={form.contact}
          sourceGroups={sourceGroups}
          submitting={add.isPending || update.isPending}
          onClose={() => setForm(null)}
          onSubmit={submitForm}
        />
      ) : null}

      {uploadOpen ? (
        <UploadCsvModal
          preview={(file, options) => previewImport.mutateAsync({ file, options })}
          commit={(preview) => confirmImport.mutateAsync(preview)}
          sourceGroups={sourceGroups}
          isAdmin={isAdmin}
          viewer={{
            id: profile.id,
            name: profile.display_name || profile.email || "You",
            email: profile.email || "",
            practice: profile.primary_practice || null,
            title: profile.title || null,
          }}
          consultants={assignableUsers.data ?? []}
          consultantsLoading={assignableUsers.isLoading}
          onDone={(imported) => {
            setUploadOpen(false);
            setNote(`Imported ${imported.toLocaleString()} contact${imported === 1 ? "" : "s"}.`);
          }}
          onClose={() => setUploadOpen(false)}
        />
      ) : null}
    </div>
  );
}
